import { ReactNode } from "react";
import { useNavigate } from "react-router-dom";
import { CheckCircle2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useApplyContext } from "@/hooks/use-apply-context";
import PersonalLoanFormLayout from "@/components/PersonalLoanFormLayout";

interface Props {
  children: ReactNode;
}

/**
 * Shown at the entry point of the apply funnel when a user re-clicks a personalized
 * link (custom_id) for an already-submitted application. Offers two paths:
 *  1. Review previous offer results
 *  2. Resubmit — proceeds through the standard funnel as a new submission
 */
const ApplyAlreadySubmittedGate = ({ children }: Props) => {
  const { alreadySubmitted, latestApplicationId, resubmitRequested, setResubmitRequested, basePath } = useApplyContext();
  const navigate = useNavigate();

  if (!alreadySubmitted || resubmitRequested) {
    return <>{children}</>;
  }

  const handleViewResults = () => {
    if (latestApplicationId) {
      navigate(`${basePath}/offers-result/${latestApplicationId}`);
    }
  };

  const handleResubmit = () => {
    setResubmitRequested(true);
  };

  return (
    <PersonalLoanFormLayout currentStep={1} totalSteps={11}>
      <div className="text-center space-y-6">
        <div className="flex justify-center">
          <div className="rounded-full bg-primary/10 p-4">
            <CheckCircle2 className="h-12 w-12 text-primary" aria-hidden="true" />
          </div>
        </div>
        <div className="space-y-2">
          <h1 className="text-2xl md:text-3xl font-bold text-foreground">
            You've already submitted this application
          </h1>
          <p className="text-muted-foreground">
            Review your previous results or submit a new application below.
          </p>
        </div>
        <div className="space-y-3 pt-2">
          <Button
            variant="hero"
            size="lg"
            className="w-full"
            onClick={handleViewResults}
            disabled={!latestApplicationId}
          >
            Review my results
          </Button>
          <Button
            variant="outline"
            size="lg"
            className="w-full"
            onClick={handleResubmit}
          >
            Submit a new application
          </Button>
        </div>
      </div>
    </PersonalLoanFormLayout>
  );
};

export default ApplyAlreadySubmittedGate;
